/**
 * SSI Override Banner
 *
 * Informational banner shown on RD_SSI (and optionally SSI proforma pages)
 * when the Lead clinician has taken over the Semi-Structured Interview module.
 *
 * Visual style matches SSIResetBanner (amber, left-aligned, ExclamationIcon).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ExclamationIcon } from '@heroicons/react/solid';

/**
 * @param {Object} props
 * @param {boolean} props.isVisible
 * @param {string} [props.originalClinicianName]
 * @param {string} [props.snapshotPath] - Route to the read-only original record
 * @param {boolean} [props.isCurrentUserLead=true] - Tailors copy for Lead vs others
 */
const SSIOverrideBanner = ({
  isVisible = false,
  originalClinicianName = 'the original SSI clinician',
  snapshotPath = null,
  isCurrentUserLead = true,
}) => {
  return (
    <div
      className={`overflow-hidden transition-all duration-500 ease-in-out ${
        isVisible ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="bg-amber-50 rounded-2xl p-4 text-left">
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-1">
            <ExclamationIcon className="h-5 w-5 text-amber-600" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-md font-medium text-amber-800">
              {isCurrentUserLead
                ? 'You have taken over this module'
                : 'Lead clinician has taken over this module'}
            </h3>
            <div className="mt-2 text-sm text-amber-700">
              {isCurrentUserLead ? (
                <>
                  <p className="mb-2">
                    You have taken over the Semi-Structured Interview from{' '}
                    <strong>{originalClinicianName}</strong>. If you change
                    symptom or day-to-day impact decisions, any affected AI
                    paragraphs will need to be approved again before the
                    Patient Report can be finalised.
                  </p>
                  <p className="mb-2">
                    The patient report will continue to name{' '}
                    <strong>{originalClinicianName}</strong> as the clinician who
                    completed this appointment, with the original appointment date.
                  </p>
                </>
              ) : (
                <p className="mb-2">
                  The Lead clinician has taken over editing and AI writing review
                  for this Semi-Structured Interview. You can still view the
                  original answers, but you can no longer edit decisions or
                  approve paragraphs for this module.
                </p>
              )}
              {snapshotPath && (
                <p>
                  <Link
                    to={snapshotPath}
                    className="font-semibold text-amber-800 underline hover:text-amber-900"
                  >
                    View original answers
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSIOverrideBanner;
